import { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Menu, X, ChevronRight } from 'lucide-react'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Blog', href: '/blog' },
    { name: 'Help Center', href: '/help-center' },
    { name: 'Gift Card', href: '/gift-card' },
    { name: 'Contact', href: '/contact' },
  ]

  const closeMenu = useCallback(() => {
    if (!isMenuOpen || isClosing) return
    setIsClosing(true)
    setTimeout(() => {
      setIsMenuOpen(false)
      setIsClosing(false)
    }, 220)
  }, [isMenuOpen, isClosing])

  const openMenu = () => {
    setIsClosing(false)
    setIsMenuOpen(true)
  }

  const toggleMenu = () => {
    if (isMenuOpen) closeMenu()
    else openMenu()
  }

  // Close on click outside: overlay handles it. Close on escape key.
  useEffect(() => {
    if (!isMenuOpen) return
    const onEscape = (e) => {
      if (e.key === 'Escape') closeMenu()
    }
    document.addEventListener('keydown', onEscape)
    return () => document.removeEventListener('keydown', onEscape)
  }, [isMenuOpen, closeMenu])

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0" style={{ zIndex: 101 }}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top row above overlay so X and logo stay clickable */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/media/images/logo/lumoralogo.png"
              alt="Spa~Radise Luxe Logo"
              className="w-12 h-12 object-contain rounded-full border border-purple-200 shadow-sm"
            />
            <span className="text-xl font-serif font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-pink-400 to-rose-400">
              Spa~Radise Luxe
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200"
              >
                {item.name}
              </Link>
            ))}
            <div className="flex items-center space-x-4">
              <span className="hidden lg:inline text-sm text-gray-500">
                Indulge in serene spa moments
              </span>
              <Link
                to="/services"
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-400 hover:from-purple-600 hover:via-pink-600 hover:to-rose-500 text-white font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Book a Spa Escape
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-purple-600 transition-colors duration-200"
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile: overlay + dropdown rendered in portal so fixed covers full viewport and backdrop is clickable */}
        {isMenuOpen &&
          createPortal(
            <div className="md:hidden fixed inset-0" style={{ zIndex: 99 }}>
              {/* Full-screen backdrop – visible and clickable to close */}
              <button
                type="button"
                aria-label="Close menu"
                onClick={closeMenu}
                className={`absolute inset-0 w-full h-full bg-black/50 transition-opacity duration-200 ease-out ${
                  isClosing ? 'opacity-0' : 'opacity-100'
                }`}
                style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              />
              {/* Dropdown panel – above backdrop, clicks don't close */}
              <div
                onClick={(e) => e.stopPropagation()}
                className={`absolute left-4 right-4 rounded-2xl shadow-xl border border-gray-200/80 bg-white overflow-hidden transition-all duration-200 ease-out ${
                  isClosing
                    ? 'opacity-0 -translate-y-2 pointer-events-none'
                    : 'opacity-100 translate-y-0'
                }`}
                style={{ top: '5rem', zIndex: 1 }}
              >
                <div className="py-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={closeMenu}
                      className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors duration-200"
                    >
                      <span className="font-medium">{item.name}</span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </Link>
                  ))}
                  <div className="border-t border-gray-100 mt-2 pt-2 px-4 pb-3">
                    <Link
                      to="/services"
                      onClick={closeMenu}
                      className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-rose-400 hover:from-purple-600 hover:via-pink-600 hover:to-rose-500 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-300"
                    >
                      Book a Spa Escape
                    </Link>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
      </nav>
    </header>
  )
}

export default Header
